// Deterministic scoring arithmetic of the AI-powered curation model
// mirrored from app/services/curation/score_calculator.rb and
// curation/gate_check.rb; a weight change has to land on both sides.
// The client recomputes on every override for instant feedback; persisted
// scores are always recomputed server-side.

export type SkillState = 'explicit' | 'inferred' | 'not_found';
export type SkillGroup = 'must_have' | 'nice_to_have';
export type GateResult = 'pass' | 'fail' | 'na';

export interface AiSkill {
  evidence?: string;
  group: SkillGroup;
  name: string;
  state: SkillState;
}

export interface ContextFactor {
  id: string;
  matched: boolean;
  note?: string;
}

export interface AiPayload {
  experience: {
    context: { factors: ContextFactor[]; reasoning?: string } | null;
    relevant_years: number | null;
  } | null;
  flags: { detail: string; type: string }[];
  skills: AiSkill[];
}

export interface GateCheck {
  detail: Record<string, unknown>;
  key: string;
  kind: 'location' | 'language';
  override: GateResult | null;
  verdict: GateResult;
}

export interface DirectOverrides {
  context_pct: number | null;
  skills_pct: number | null;
  years_pct: number | null;
}

export interface EffectiveScores {
  category: 'qualified' | 'probably_not_a_match' | null;
  computedContext: number | null;
  computedSkills: number | null;
  computedYears: number | null;
  context: number | null;
  experience: number | null;
  mustHaveAvg: number | null;
  niceToHaveAvg: number | null;
  overall: number | null;
  skills: number | null;
  years: number | null;
}

export const SKILL_WEIGHTS: Record<SkillState, number> = {
  explicit: 1.0,
  inferred: 0.7,
  not_found: 0.0,
};

export const SKILL_STATE_CYCLE: SkillState[] = ['explicit', 'inferred', 'not_found'];

export const MUST_HAVE_WEIGHT = 0.7;
export const NICE_TO_HAVE_WEIGHT = 0.3;
export const YEARS_WEIGHT = 0.7;
export const CONTEXT_WEIGHT = 0.3;
export const SKILLS_WEIGHT = 0.65;
export const EXPERIENCE_WEIGHT = 0.35;
export const CONTEXT_BASE = 50;
export const CONTEXT_BONUSES: Record<string, number> = {
  company_type: 15,
  industry: 20,
  role: 15,
  tech_stack: 10,
};
export const QUALIFIED_THRESHOLD = 65;

export const nextSkillState = (state: SkillState): SkillState => {
  const index = SKILL_STATE_CYCLE.indexOf(state);
  return SKILL_STATE_CYCLE[(index + 1) % SKILL_STATE_CYCLE.length];
};

// Weighted combination that renormalizes when a component is missing, so one
// absent group never drags the score to zero.
const combine = (components: [number | null, number][]): number | null => {
  const present = components.filter(([value]) => value !== null) as [number, number][];
  if (present.length === 0) return null;

  const totalWeight = present.reduce((sum, [, weight]) => sum + weight, 0);
  return present.reduce((sum, [value, weight]) => sum + value * weight, 0) / totalWeight;
};

export const groupAverage = (
  skills: AiSkill[],
  group: SkillGroup,
  skillStates: Record<string, SkillState>,
): number | null => {
  const weights = skills
    .filter((skill) => skill.group === group)
    .map((skill) => SKILL_WEIGHTS[skillStates[skill.name] || skill.state]);
  if (weights.length === 0) return null;

  return (weights.reduce((sum, weight) => sum + weight, 0) / weights.length) * 100;
};

// Spec table: within the required range 100, off by up to 1 year 80, up to 2
// years 60, well outside 40. A missing bound is open-ended.
export const yearsScore = (
  relevantYears: number | null,
  experienceMin: number | null,
  experienceMax: number | null,
): number | null => {
  if (relevantYears === null) return null;
  if (experienceMin === null && experienceMax === null) return null;

  let distance = 0;
  if (experienceMin !== null && relevantYears < experienceMin) distance = experienceMin - relevantYears;
  if (experienceMax !== null && relevantYears > experienceMax) distance = relevantYears - experienceMax;

  if (distance === 0) return 100;
  if (distance <= 1) return 80;
  if (distance <= 2) return 60;
  return 40;
};

export const contextScore = (factors: ContextFactor[] | null): number | null => {
  if (factors === null) return null;

  const bonus = factors.reduce(
    (sum, factor) => sum + (factor.matched ? CONTEXT_BONUSES[factor.id] || 0 : 0),
    0,
  );
  return Math.min(CONTEXT_BASE + bonus, 100);
};

export const category = (overall: number | null): EffectiveScores['category'] => {
  if (overall === null) return null;
  return overall >= QUALIFIED_THRESHOLD ? 'qualified' : 'probably_not_a_match';
};

export const effectiveScores = (
  payload: AiPayload | null,
  skillStates: Record<string, SkillState>,
  direct: DirectOverrides,
  experienceMin: number | null,
  experienceMax: number | null,
): EffectiveScores => {
  const skills = payload ? payload.skills : [];
  const mustHaveAvg = groupAverage(skills, 'must_have', skillStates);
  const niceToHaveAvg = groupAverage(skills, 'nice_to_have', skillStates);
  const computedSkills = combine([
    [mustHaveAvg, MUST_HAVE_WEIGHT],
    [niceToHaveAvg, NICE_TO_HAVE_WEIGHT],
  ]);
  const computedYears = yearsScore(
    payload && payload.experience ? payload.experience.relevant_years : null,
    experienceMin,
    experienceMax,
  );
  const computedContext = contextScore(
    payload && payload.experience && payload.experience.context
      ? payload.experience.context.factors
      : null,
  );

  const skillsPct = direct.skills_pct !== null ? direct.skills_pct : computedSkills;
  const years = direct.years_pct !== null ? direct.years_pct : computedYears;
  const context = direct.context_pct !== null ? direct.context_pct : computedContext;
  const experience = combine([
    [years, YEARS_WEIGHT],
    [context, CONTEXT_WEIGHT],
  ]);
  const overall = combine([
    [skillsPct, SKILLS_WEIGHT],
    [experience, EXPERIENCE_WEIGHT],
  ]);

  return {
    category: category(overall),
    computedContext,
    computedSkills,
    computedYears,
    context,
    experience,
    mustHaveAvg,
    niceToHaveAvg,
    overall,
    skills: skillsPct,
    years,
  };
};

export const effectiveGateVerdict = (check: GateCheck): GateResult =>
  check.override !== null ? check.override : check.verdict;

export interface GateOutcome {
  canContinue: boolean;
  decision: 'gate_passed' | 'gate_failed';
  unresolvedNa: GateCheck[];
}

export const gateOutcome = (checks: GateCheck[]): GateOutcome => {
  const failed = checks.some((check) => effectiveGateVerdict(check) === 'fail');
  const unresolvedNa = checks.filter((check) => effectiveGateVerdict(check) === 'na');

  return {
    canContinue: !failed,
    decision: failed ? 'gate_failed' : 'gate_passed',
    unresolvedNa,
  };
};

// Internals stay unrounded floats; round only at render time.
export const displayPct = (value: number | null): string =>
  value === null ? '—' : `${Math.round(value)}%`;
